import { Table, TableHead, TableRow, TableCell, TableBody, Box, Tooltip } from '@mui/material';
import { Checkbox, Icon, IconButton } from '@sm-dashboard/ui-components';
import { GridProps, ColumnType } from '@sm-dashboard/types';
import React, { useState, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { getColumnWidth, rowStableKey, separateActionsByLevel, separateColumnsByDisplayLevel } from '../helpFunctions';
import { alpha, useTheme } from '@mui/material/styles';
import DesktopGridRow from './DesktopGridRow';
import { SelectionActionsFloatingBar } from './SelectionActionsFloatingBar';
const extractReactNodeText = (node: React.ReactNode): string => {
  if (node == null) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractReactNodeText).join('');
  if (React.isValidElement(node)) return extractReactNodeText((node as React.ReactElement).props.children);
  return '';
};

const DesktopGridView = <T, U>({
  data,
  columns,
  expanded,
  onRowClick,
  actions,
  style,
  components,
  selectable,
  selectActionsRender,
  hideSelectActionsBar,
  editable,
  editActionsHandler,
  editInputRender,
  selectedRows,
  setSelectedRows,
  selectedAll,
  setSelectedAll,
  resetSelections,
  onSort,
  sortField,
  sortDirection,
  sortable: gridSortable,
  virtualizeRows,
  tableRef
}: GridProps<T, U> & {
  selectedRows: number[];
  setSelectedRows: (rows: number[]) => void;
  selectedAll: boolean;
  setSelectedAll: (all: boolean) => void;
  resetSelections: () => void;
}) => {
  const { childRowsKey, onExpand, renderExpanded, desktopSideBottomColumns, isRowExpandable, panelLayout } =
    expanded || {};
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; columnIndex: number } | null>(null);
  const [editedValue, setEditedValue] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState<number | null>(null);

  // מדידה דינמית של מיקום העמודה הראשונה
  const firstColumnRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const separatedColumns = useMemo(() => separateColumnsByDisplayLevel(columns, false), [columns]);
  const expandable =
    !onRowClick &&
    (separatedColumns['expanded']?.length > 0 ||
      separatedColumns['bottom']?.length > 0 ||
      separatedColumns['standard']?.length > 0 ||
      actions?.some(action => action.level === 'regular') ||
      renderExpanded !== undefined);

  // קבלת קולמנס ראשיים + הוספת עמודת toggle אם צריך
  const primaryColumns = useMemo(() => separatedColumns['primary'], [separatedColumns]);
  const columnWidths = useMemo(
    () => primaryColumns.map(column => getColumnWidth(column, data)),
    [primaryColumns, data]
  );
  const flexColumnIndex = useMemo(() => primaryColumns.findIndex(column => column.flexGrow), [primaryColumns]);

  const [primaryActions, regularActions] = useMemo(() => separateActionsByLevel(actions || [], false), [actions]);

  const isColumnSortable = (column: GridProps<T, U>['columns'][number]): boolean => {
    // Only allow sorting for specific column types: text, number, amount, date, dateTime
    const allowedTypes: Array<ColumnType> = ['text', 'number', 'amount', 'date', 'dateTime'];
    if (column.type && !allowedTypes.includes(column.type)) {
      return false;
    }

    if (column.type === 'mailingApproval') {
      return false;
    }

    if (data && data.length > 0) {
      let sampleValue: any;
      if (column.valueGetter) {
        sampleValue = column.valueGetter(data[0]);
      } else {
        sampleValue = (data[0] as any)[column.field];
      }
      if (typeof sampleValue === 'boolean') {
        return false;
      }
    }

    if (gridSortable === false) {
      return false;
    }
    if (gridSortable === true) {
      return column.sortable !== false;
    }
    return column.sortable === true;
  };
  // Calculate the remaining width of the screen after accounting for column widths
  const [restWidth, isRestLessThan20Percent] = useMemo(() => {
    const totalColumnWidth = columnWidths.reduce((sum, col) => sum + (col?.width || 0), 0);
    const containerWidth =
      firstColumnRef.current?.parentElement?.offsetWidth || firstColumnRef.current?.offsetWidth || window.innerWidth;
    const rest = Math.max(containerWidth - totalColumnWidth, 0);
    const isLessThan20Percent = rest < 0.2 * containerWidth;
    return [rest, isLessThan20Percent];
  }, [columnWidths, firstColumnRef.current]);

  // A column that opts into `flexGrow` claims all of the leftover width itself, instead of a third
  // of it being wasted on the invisible alignment cell below — grids that don't opt any column in
  // keep the exact original behavior.
  const alignColumnsCellSxProps =
    flexColumnIndex !== -1
      ? { width: '0px', padding: 0 }
      : { width: isRestLessThan20Percent ? '0px' : restWidth / 3, padding: 0 };

  const effectiveColumnWidths = useMemo(() => {
    if (flexColumnIndex === -1 || restWidth <= 0) return columnWidths;
    return columnWidths.map((columnWidth, index) =>
      index === flexColumnIndex
        ? {
            ...columnWidth,
            width: (columnWidth?.width || 0) + restWidth,
            maxWidth: (columnWidth?.maxWidth || 0) + restWidth
          }
        : columnWidth
    );
  }, [columnWidths, flexColumnIndex, restWidth]);

  const handleSelection = (index: number) => {
    const newSelection = selectedRows?.includes(index)
      ? selectedRows.filter(i => i !== index)
      : [...(selectedRows || []), index];
    setSelectedRows(newSelection);
  };

  // ── Row virtualization (opt-in) ────────────────────────────────────────────
  // When enabled, only rows in (and near) the viewport are mounted. Uses the spacer-row
  // technique so the native <table> layout, sticky header and column alignment are preserved.
  // Requires a scroll container (tableRef) — falls back to rendering all rows without it.
  const useRowVirtualization = Boolean(virtualizeRows && tableRef);
  const rowHeight = style?.rowHeight ?? 40;
  const virtualizer = useVirtualizer({
    count: data?.length ?? 0,
    getScrollElement: () => tableRef?.current ?? null,
    estimateSize: () => rowHeight,
    overscan: 10,
    getItemKey: index => rowStableKey(data?.[index], index),
    enabled: useRowVirtualization
  });

  const virtualItems = useRowVirtualization ? virtualizer.getVirtualItems() : [];
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom = virtualItems.length > 0 ? totalSize - virtualItems[virtualItems.length - 1].end : 0;

  const handleSortClick = (field: string) => {
    if (!onSort) return;

    if (sortField === field) {
      if (sortDirection === 'asc') {
        onSort(field, 'desc');
      } else if (sortDirection === 'desc') {
        onSort(field, null);
      } else {
        onSort(field, 'asc');
      }
    } else {
      onSort(field, 'asc');
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField === field && sortDirection === 'asc') {
      return <Icon icon="arrowDownward" color="inherit" />;
    }
    if (sortField === field && sortDirection === 'desc') {
      return <Icon icon="arrowUpward" color="inherit" />;
    }

    return (
      <Box sx={{ opacity: 0.5, display: 'flex' }}>
        <Icon icon="unfoldMore" color="inherit" />
      </Box>
    );
  };

  const renderRow = (row: T, index: number, measureRef?: (el: HTMLElement | null) => void) => (
    <DesktopGridRow
      key={rowStableKey(row, index)}
      row={row}
      index={index}
      columns={columns}
      actions={actions}
      columnWidths={effectiveColumnWidths}
      expandable={expandable}
      rowExpandable={expandable && (isRowExpandable ? isRowExpandable(row) : true)}
      onExpand={onExpand}
      onRowClick={onRowClick}
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
      selectable={selectable}
      selectedRows={selectedRows}
      handleSelection={handleSelection}
      components={components}
      firstColumnRef={firstColumnRef}
      measureRef={measureRef}
      alignColumnsCellSxProps={alignColumnsCellSxProps}
      style={style}
      editable={editable}
      editingCell={editingCell}
      setEditingCell={setEditingCell}
      editedValue={editedValue}
      setEditedValue={setEditedValue}
      editActionsHandler={editActionsHandler}
      editInputRender={editInputRender}
      childRowsKey={childRowsKey}
      renderExpanded={
        renderExpanded as
          | ((row: T, childRow: U | undefined, childIndex: number, mobileView: boolean) => React.ReactNode)
          | undefined
      }
      desktopSideBottomColumns={desktopSideBottomColumns}
      panelLayout={panelLayout}
    />
  );

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Without the floating bar the page renders its own selection UI, so it is also called with an
          empty selection — otherwise the page never hears that the last row was deselected. */}
      {selectable &&
        selectActionsRender &&
        (hideSelectActionsBar
          ? selectActionsRender(selectedRows ?? [], selectedAll, resetSelections, data)
          : selectedRows?.length > 0 && (
              <SelectionActionsFloatingBar count={selectedRows.length}>
                {selectActionsRender(selectedRows, selectedAll, resetSelections, data)}
              </SelectionActionsFloatingBar>
            ))}
      <Table stickyHeader>
        <TableHead>
          <TableRow
            sx={{
              height: '45px',
              '& .MuiTableCell-root': {
                // Must be opaque so body rows don't show under sticky header while scrolling.
                backgroundColor: theme.palette.background.default,
                zIndex: 1000,
                fontWeight: 700,
                fontSize: '14px',
                color: theme.palette.text.secondary,
                borderBottom: `2px solid ${alpha(theme.palette.text.primary, 0.05)}`,
                py: 0,
                '&:first-of-type': {
                  borderTopLeftRadius: 0
                },
                '&:last-of-type': {
                  borderTopRightRadius: 0
                }
              }
            }}
          >
            {/* Selection Column */}
            {selectable && (
              <TableCell
                padding="checkbox"
                sx={{
                  width: 28,
                  minWidth: 28,
                  maxWidth: 28,
                  px: 0.5
                }}
              >
                <Checkbox
                  size="small"
                  indeterminate={selectedRows?.length > 0 && selectedRows?.length < (data?.length || 0)}
                  checked={selectedAll}
                  onChange={() => {
                    setSelectedRows(selectedAll ? [] : Array.from({ length: data?.length || 0 }, (_, i) => i));
                    setSelectedAll(!selectedAll);
                  }}
                />
              </TableCell>
            )}

            {/* Row Icon */}
            {components?.rowIcon && <TableCell />}

            {/* Add Button */}
            {components?.AddButton && <TableCell>{components.AddButton}</TableCell>}

            {/* Data Columns */}
            {primaryColumns.map((column, index) => {
              const columnMeta = column as { description?: string; headerTooltip?: string };
              const headerTooltip = columnMeta.description || columnMeta.headerTooltip || '';
              const nameTooltip =
                typeof column.headerName === 'string' || typeof column.headerName === 'number'
                  ? String(column.headerName)
                  : extractReactNodeText(column.headerName);

              return (
                <TableCell sx={{ ...effectiveColumnWidths[index] }} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip
                      title={nameTooltip ? `${nameTooltip}` : ''}
                      placement="top"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                            boxShadow: 5,
                            px: 1,
                            py: 0.5,
                            fontWeight: 700
                          }
                        },
                        arrow: { sx: { color: theme.palette.background.paper } }
                      }}
                    >
                      {headerTooltip ? (
                        <Tooltip title={headerTooltip} placement="bottom">
                          <Box
                            component="span"
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {column.headerName}
                          </Box>
                        </Tooltip>
                      ) : (
                        <Box
                          component="span"
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {column.headerName}
                        </Box>
                      )}
                    </Tooltip>

                    {isColumnSortable(column) && onSort && (
                      <IconButton
                        variant="text"
                        color="secondary"
                        shape="circle"
                        size="small"
                        aria-label="מיון"
                        onClick={() => handleSortClick(column.field as string)}
                      >
                        {getSortIcon(column.field as string)}
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              );
            })}

            {/* Cell for align all visibleColumns to the start */}
            <TableCell sx={{ ...alignColumnsCellSxProps }} />

            {/* Primary Actions */}
            {primaryActions &&
              primaryActions.length > 0 &&
              primaryActions.map((action, index) => <TableCell key={`action-${index}`}>{action.label}</TableCell>)}

            {/* Auto Expansion Toggle */}
            {expandable && <TableCell width={'40px'} />}
          </TableRow>
        </TableHead>

        <TableBody>
          {data && data.length > 0 && useRowVirtualization ? (
            <>
              {paddingTop > 0 && (
                <TableRow aria-hidden>
                  <TableCell colSpan={12} sx={{ height: paddingTop, p: 0, border: 'none' }} />
                </TableRow>
              )}
              {virtualItems.map(virtualRow =>
                renderRow(data[virtualRow.index], virtualRow.index, virtualizer.measureElement)
              )}
              {paddingBottom > 0 && (
                <TableRow aria-hidden>
                  <TableCell colSpan={12} sx={{ height: paddingBottom, p: 0, border: 'none' }} />
                </TableRow>
              )}
            </>
          ) : (
            data && data.length > 0 && data.map((row: T, index: number) => renderRow(row, index))
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

export default DesktopGridView;
